<template>
  <ImageBar title="最新动态" height="80px" />
  <div class="home-news-detail" v-loading="loading">
    <div v-if="news" class="news-container">
      <el-button class="back-btn" @click="goBack">
        <el-icon><ArrowLeft /></el-icon>
        返回首页
      </el-button>

      <article class="news-article">
        <h1>{{ news.title }}</h1>
        <p class="news-meta">{{ formatDate(news.createdAt) }}</p>
        <p v-if="news.excerpt" class="news-excerpt">{{ news.excerpt }}</p>

        <el-image
          v-if="news.imageUrl"
          :src="news.imageUrl"
          fit="cover"
          class="news-cover"
        >
          <template #error>
            <div class="image-slot">
              <el-icon><Picture /></el-icon>
            </div>
          </template>
        </el-image>

        <div class="news-body markdown-body" v-html="renderedContent" />
      </article>
    </div>

    <el-empty v-else-if="!loading" description="动态不存在或已下架" />
  </div>
</template>

<script>
import axios from 'axios'
import { nextTick } from 'vue'
import { ArrowLeft, Picture } from '@element-plus/icons-vue'
import ImageBar from '@/components/ImageBar.vue'
import { renderMarkdown } from '@/utils/markdown'
import { scrollPageToTop } from '@/utils/homeScroll'

export default {
  name: 'HomeNewsDetailView',
  components: { ArrowLeft, Picture, ImageBar },
  data() {
    return {
      loading: false,
      news: null
    }
  },
  computed: {
    renderedContent() {
      return renderMarkdown(this.news?.content)
    }
  },
  watch: {
    '$route.params.id'() {
      this.fetchDetail()
    }
  },
  mounted() {
    scrollPageToTop()
  },
  created() {
    scrollPageToTop()
    this.fetchDetail()
  },
  methods: {
    async fetchDetail() {
      const id = this.$route.params.id
      if (!id) {
        return
      }
      scrollPageToTop()
      this.loading = true
      try {
        const response = await axios.get(`/api/v1/home/news/${id}`)
        if (response.data?.code === 200 && response.data.data) {
          this.news = response.data.data
        } else {
          this.news = null
        }
      } catch (error) {
        console.error('获取动态详情失败:', error)
        this.news = null
      } finally {
        this.loading = false
        await nextTick()
        scrollPageToTop()
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(Number(timestamp) * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    goBack() {
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.home-news-detail {
  min-height: 60vh;
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 20px 48px;
}
.back-btn {
  margin-bottom: 20px;
}
.news-article h1 {
  font-size: 28px;
  margin: 0 0 12px;
  line-height: 1.35;
}
.news-meta {
  color: #909399;
  font-size: 14px;
  margin: 0 0 16px;
}
.news-excerpt {
  font-size: 16px;
  color: #606266;
  line-height: 1.6;
  margin: 0 0 20px;
}
.news-cover {
  width: 100%;
  max-height: 400px;
  border-radius: 8px;
  margin-bottom: 24px;
}
.news-body {
  font-size: 16px;
  line-height: 1.8;
  color: #303133;
  word-break: break-word;
}
.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  background: #f5f7fa;
}

:deep(.markdown-body h1),
:deep(.markdown-body h2),
:deep(.markdown-body h3) {
  margin: 1.2em 0 0.6em;
  line-height: 1.4;
  color: #303133;
}
:deep(.markdown-body p) {
  margin: 0 0 1em;
}
:deep(.markdown-body ul),
:deep(.markdown-body ol) {
  margin: 0 0 1em;
  padding-left: 1.5em;
}
:deep(.markdown-body pre) {
  overflow: auto;
  padding: 12px;
  border-radius: 6px;
  background: #f5f7fa;
  margin-bottom: 1em;
}
:deep(.markdown-body code) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
:deep(.markdown-body :not(pre) > code) {
  padding: 0.15em 0.35em;
  border-radius: 4px;
  background: #f5f7fa;
}
:deep(.markdown-body table) {
  display: block;
  width: 100%;
  overflow: auto;
  margin: 0 0 16px;
  border-spacing: 0;
  border-collapse: collapse;
}
:deep(.markdown-body table th) {
  background-color: var(--el-fill-color-light);
  color: var(--el-text-color-primary);
  font-weight: 600;
  padding: 8px 13px;
  border: 1px solid var(--el-border-color);
  white-space: nowrap;
}
:deep(.markdown-body table td) {
  padding: 8px 13px;
  border: 1px solid var(--el-border-color);
}
:deep(.markdown-body table tr) {
  background-color: var(--el-bg-color);
  border-top: 1px solid var(--el-border-color);
}
:deep(.markdown-body table tr:nth-child(2n)) {
  background-color: var(--el-fill-color-lighter);
}
:deep(.markdown-body blockquote) {
  margin: 0 0 1em;
  padding: 0 1em;
  color: #606266;
  border-left: 4px solid #dcdfe6;
}
</style>
